/**
 * API Service - Frontend HTTP Client
 * Handles all API communication with the backend
 */

// Configure this to your backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://15.235.117.70:5001/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('hatimi_token');
  }

  // Set auth token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('hatimi_token', token);
    } else {
      localStorage.removeItem('hatimi_token');
    }
  }

  // Get auth token
  getToken() {
    return this.token || localStorage.getItem('hatimi_token');
  }

  // Check if logged in
  isAuthenticated() {
    return !!this.getToken();
  }

  // Logout
  logout() {
    this.setToken(null);
    localStorage.removeItem('hatimi_user');
  }

  // Make API request
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - Unauthorized
        if (response.status === 401) {
          this.logout();
          window.location.reload();
        }
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // GET request
  get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST request
  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // ============= AUTH ENDPOINTS =============
  async login(username, password) {
    const data = await this.post('/auth/login', { username, password });
    if (data.token) {
      this.setToken(data.token);
      localStorage.setItem('hatimi_user', JSON.stringify(data.user));
    }
    return data;
  }

  async getCurrentUser() {
    return this.get('/auth/me');
  }

  async changePassword(currentPassword, newPassword) {
    return this.post('/auth/change-password', { currentPassword, newPassword });
  }

  // ============= MEMBERS ENDPOINTS =============
  async getMembers() {
    return this.get('/members');
  }

  async getMember(id) {
    return this.get(`/members/${id}`);
  }

  async searchMemberByIts(itsId) {
    return this.get(`/members/search/${itsId}`);
  }

  async createMember(data) {
    return this.post('/members', data);
  }

  async updateMember(id, data) {
    return this.put(`/members/${id}`, data);
  }

  async deleteMember(id) {
    return this.delete(`/members/${id}`);
  }

  // ============= RECEIPTS ENDPOINTS =============
  async getReceipts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/receipts${queryString ? `?${queryString}` : ''}`);
  }

  async getReceipt(id) {
    return this.get(`/receipts/${id}`);
  }

  async getNextReceiptNumber() {
    return this.get('/receipts/next-number');
  }

  async createReceipt(data) {
    return this.post('/receipts', data);
  }

  async updateReceipt(id, data) {
    return this.put(`/receipts/${id}`, data);
  }

  async deleteReceipt(id) {
    return this.delete(`/receipts/${id}`);
  }

  // ============= EXPENSE CATEGORIES ENDPOINTS (Admin Only) =============
  async getCategories() {
    return this.get('/categories');
  }

  async getCategory(id) {
    return this.get(`/categories/${id}`);
  }

  async createCategory(data) {
    return this.post('/categories', data);
  }

  async updateCategory(id, data) {
    return this.put(`/categories/${id}`, data);
  }

  async deleteCategory(id) {
    return this.delete(`/categories/${id}`);
  }

  async reorderCategory(id, newOrder) {
    return this.put(`/categories/${id}/reorder`, { new_order: newOrder });
  }

  async getCategoryStats() {
    return this.get('/categories/stats/usage');
  }

  // ============= EXPENSES ENDPOINTS =============
  async getExpenseCategories() {
    return this.get('/expenses/categories');
  }

  async getExpenses(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/expenses${queryString ? `?${queryString}` : ''}`);
  }

  async getExpense(id) {
    return this.get(`/expenses/${id}`);
  }

  async getNextVoucherNumber() {
    return this.get('/expenses/next-number');
  }

  async createExpense(data) {
    return this.post('/expenses', data);
  }

  async updateExpense(id, data) {
    return this.put(`/expenses/${id}`, data);
  }

  async deleteExpense(id) {
    return this.delete(`/expenses/${id}`);
  }

  // ============= REPORTS ENDPOINTS =============
  async getDashboard() {
    return this.get('/reports/dashboard');
  }

  async getCashbook(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/reports/cashbook${queryString ? `?${queryString}` : ''}`);
  }

  async getIncomeExpenditure() {
    return this.get('/reports/income-expenditure');
  }

  async getTakhmeenReport() {
    return this.get('/reports/takhmeen');
  }

  async getDailyReport(date) {
    return this.get(`/reports/daily/${date}`);
  }

  // ============= SETTINGS ENDPOINTS =============
  async getSettings() {
    return this.get('/settings');
  }

  async updateSettings(settings) {
    return this.post('/settings', settings);
  }

  // ============= USERS ENDPOINTS (Admin only) =============
  async getUsers() {
    return this.get('/users');
  }

  async createUser(data) {
    return this.post('/users', data);
  }

  async updateUser(id, data) {
    return this.put(`/users/${id}`, data);
  }

  async deleteUser(id) {
    return this.delete(`/users/${id}`);
  }

  // ============= EXTERNAL APIS =============
  // Fetch Takhmeen data from external API
  async fetchTakhmeenData(itsId) {
    try {
      const response = await fetch(`https://hatimihills.datadove.tech/api/TakhmeenDataForHOF/${itsId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch Takhmeen data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching Takhmeen data:', error);
      throw error;
    }
  }

  // Fetch Family data from external API
  async fetchFamilyData(hofId) {
    try {
      const response = await fetch(`https://hatimihills.datadove.tech/api/FamilyForHOF/${hofId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch family data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching family data:', error);
      throw error;
    }
  }
}

// Export singleton instance
const api = new ApiService();
export default api;
